import * as _ from "lodash";
import { ProgramContentType } from "data/BlockComponent";
import {
  AudioPlayerType,
  CellType,
  MultipleSelectionType,
  PushNotificationType,
  SelfCheckType,
  SingleSelectionType,
} from "data/CellComponent";
import {
  getPercentageSum,
  getTaskIdFromTaskKey,
  isAllUserFieldFilled,
} from "logic/logics";
import { UserSimField } from "api/userSimApi";

// 자동적 사고·가치 번호, 요일 명상 훈련의 요일(@), 환자 id 를 뗀 활동지 ID
const getBaseTaskId = (taskKey: string) =>
  getTaskIdFromTaskKey(taskKey).split("_")[0].split("#")[0].split("@")[0];

// 교육용 모의 내담자 계정의 마치기 알림(POST /user_sim/complete) 에서 빼는 것 — 그 밖의 활동지·생각기록지는 모두 대상 (docs/user-sim-agent.md 5장·8장)
export const USER_SIM_COMPLETE_EXCLUDED_TASK_IDS = [
  "5-7-T", // 명상훈련지
  "0-3-W", // 주차 돌아보기
];

export function isUserSimCompleteTask(taskKey: string) {
  return !USER_SIM_COMPLETE_EXCLUDED_TASK_IDS.includes(getBaseTaskId(taskKey));
}

// "자동 채우기" 에서 더 빼는 활동지 — 그 밖의 마치기 알림 대상은 모두 자동 채우기 대상
export const AUTO_FILL_EXCLUDED_TASK_IDS = [
  "10-3-A", // 목표 설정하기 — 운영자가 코치 코멘트 답변까지 직접 써서 모의 내담자의 출발점이 된다
  "90-1-A", // 요일 명상 훈련 — 마치기 알림은 보낸다 (Agent 는 제목만 남긴다)
];

export function isAutoFillTask(taskKey: string) {
  return (
    isUserSimCompleteTask(taskKey) &&
    !AUTO_FILL_EXCLUDED_TASK_IDS.includes(getBaseTaskId(taskKey))
  );
}

// 칸 위치 키 — invalidTextareaIndexAtom 의 indexKey 와 같은 형식
export function getCellKey(
  blockIndex: number,
  lineIndex: number,
  cellIndex: number
) {
  return `${blockIndex}-${lineIndex}-${cellIndex}`;
}

export type AutoFillValues = Record<string, string | number | number[]>;

// 선택지를 고르면 생기는 칸(선택지의 additionalContent)의 키.
// AG-UI STATE_DELTA 의 JSON Pointer 경로(/values/<key>)에 그대로 쓰이므로 "/"·"~" 를 넣지 않는다
export function getOptionCellKey(
  fieldKey: string,
  optionIndex: number,
  lineIndex: number,
  cellIndex: number
) {
  return `${fieldKey}.${optionIndex}.${lineIndex}-${cellIndex}`;
}

// 다음에 열리는 블록 — TaskContent.openNextBlock 과 같은 규칙. 마지막·isEnd 블록이면 undefined
export function getNextBlockIndex(data: ProgramContentType[], index: number) {
  if (index === data.length - 1 || data[index].isEnd) return undefined;
  const nextBlockIndex = data.findIndex(
    (element, blockIndex) =>
      blockIndex > index &&
      element.blockId &&
      element.blockId === data[index].nextBlockId
  );
  return nextBlockIndex > -1 ? nextBlockIndex : index + 1;
}

// 칸 위치 키가 그대로 유효한지 — 블록·줄·칸 배치가 같은지 비교
export function isSameStructure(
  a: ProgramContentType[],
  b: ProgramContentType[]
) {
  const shape = (content: ProgramContentType[]) =>
    JSON.stringify(
      content.map((block) => block.lines.map((line) => line.map((c) => c.type)))
    );
  return shape(a) === shape(b);
}

// 이번에 채울 칸 — 지금 보이는 필수 칸 (고르지 않은 분기·감정의 칸, optional 칸 제외). fields 순서 = 화면 순서
export function getAutoFillTargetFields(
  fields: UserSimField[],
  values: AutoFillValues
) {
  const chosen = (key: string) => {
    const value = values[key] ?? fields.find((f) => f.key === key)?.value;
    return Array.isArray(value) ? value : [value];
  };
  return fields.filter(
    (field) =>
      field.fill === "required" &&
      (!field.visible_if ||
        field.visible_if.options.some((option) =>
          chosen(field.visible_if!.key).includes(option)
        ))
  );
}

// 자동 채우기 진행률 — 이번에 채울 칸(getAutoFillTargetFields) 기준.
// 단계 = 필수 칸이 있는 블록, 그 블록의 필수 칸이 다 오면 끝난 단계
export function getAutoFillProgress(
  fields: UserSimField[],
  values: AutoFillValues
) {
  const required = getAutoFillTargetFields(fields, values);
  const isWritten = (field: UserSimField) => values[field.key] !== undefined;
  const blocks = Array.from(new Set(required.map((field) => field.block)));
  return {
    written: required.filter(isWritten).length,
    total: required.length,
    doneSteps: blocks.filter((block) =>
      required.filter((f) => f.block === block).every(isWritten)
    ).length,
    totalSteps: blocks.length,
  };
}

// 자가 검사 칸은 value 없이 점수(selectedScore)만 저장한다 (SelfCheckRadioButtonCell)
export function isCellEmpty(cell: CellType) {
  if (cell.type === "selfCheck") {
    return (cell.content as SelfCheckType).selectedScore === undefined;
  }
  return !`${cell.content.value ?? ""}`.trim();
}

// 자동 채우기 표시용 칸 키 — 여러 개 선택 칸의 선택지를 풀면 그 아래 줄이 당겨지므로, 칸 id 가 있으면 id 로 찾는다
export function getFilledCellKey(
  blockIndex: number,
  lineIndex: number,
  cellIndex: number,
  id?: string
) {
  return id
    ? `${blockIndex}-#${id}`
    : getCellKey(blockIndex, lineIndex, cellIndex);
}

// 자동 채우기로 새로 채워진 칸 — 표시용 칸 키 → 값 (선택지를 골라 붙은 칸 포함)
export function getFilledCells(
  before: ProgramContentType[],
  after: ProgramContentType[]
) {
  const filled: Record<string, string> = {};
  after.forEach((block, blockIndex) =>
    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        const prev = before[blockIndex]?.lines[lineIndex]?.[cellIndex];
        if (!isCellEmpty(cell) && (!prev || isCellEmpty(prev))) {
          const key = getFilledCellKey(
            blockIndex,
            lineIndex,
            cellIndex,
            cell.content.id
          );
          filled[key] = `${cell.content.value}`;
        }
      })
    )
  );
  return filled;
}

// 자동 채우기 값 그대로인지 — 여러 개 선택 칸("a&&b")은 고른 순서와 무관하게 비교
export function isAutoFilledValue(
  type: CellType["type"],
  filled?: string,
  value?: string
) {
  const normalize = (each?: string) =>
    type === "buttongroup_multipleChoice"
      ? each?.split("&&").sort().join("&&")
      : each;
  return filled !== undefined && normalize(filled) === normalize(value);
}

// 마치기 조건 검사용 — 소감 칸(코치에게 보내는 메시지)은 채워 두기만 하고 보내기는 사용자가 누르므로, 보낸 것(빈 칸)으로 본다
export function withFeedbackSent(content: ProgramContentType[]) {
  return content.map((block) => ({
    ...block,
    lines: block.lines.map((line) =>
      line.map((cell) =>
        cell.type === "feedback"
          ? { ...cell, content: { ...cell.content, value: "" } }
          : cell
      )
    ),
  }));
}

// 계약(docs/user-sim-agent.md)의 값 형식 검사 후 칸 content 로 변환. 형식이 틀리면 undefined.
// cell: 채울 칸 — 자가 검사 칸은 번호를 점수로 바꿔야 해서 넘긴다
export function toCellContent(
  field: UserSimField,
  value: unknown,
  cell?: CellType
) {
  if (field.input === "text") {
    if (typeof value !== "string" || !value.trim()) return undefined;
    // PlainTextarea 의 flush 와 같은 형식
    return { value, htmlString: `<p>${value}</p>` };
  }
  if (field.input === "percentage") {
    if (
      typeof value !== "number" ||
      !Number.isInteger(value) ||
      value < field.min ||
      value > field.max ||
      (!field.allow_zero && value === 0)
    ) {
      return undefined;
    }
    return { value: `${value}`, percentage: value };
  }
  if (field.input === "choice") {
    if (
      typeof value !== "number" ||
      !Number.isInteger(value) ||
      value < 0 ||
      value >= field.options.length
    ) {
      return undefined;
    }
    // 자가 검사 칸: 번호 → 응답 key → 점수. 역채점 문항이 있어 점수가 아니라 번호로 매긴다
    if (cell?.type === "selfCheck") {
      const { evaluation } = cell.content as SelfCheckType;
      return { selectedScore: evaluation[Object.keys(evaluation)[value]] };
    }
    return { value: field.options[value], selectedIndex: value };
  }
  if (field.input === "multi_choice") {
    if (
      !Array.isArray(value) ||
      value.length === 0 ||
      new Set(value).size !== value.length ||
      value.some(
        (index) =>
          typeof index !== "number" ||
          !Number.isInteger(index) ||
          index < 0 ||
          index >= field.options.length
      ) ||
      (field.select_count !== null && value.length !== field.select_count)
    ) {
      return undefined;
    }
    // ButtonGroupMultipleChoiceCell 과 같은 형식
    return {
      value: value.map((index) => field.options[index]).join("&&"),
      selectedIndices: value,
    };
  }
  return undefined;
}

// 선택 칸을 고른 것과 같은 다음 블록 지정 (ButtonGroupCell·SelectCell 의 handleChange)
function setChoiceNextBlockId(
  block: ProgramContentType,
  cell: CellType,
  selectedIndex: number
) {
  const option = (cell.content as SingleSelectionType).options?.[selectedIndex];
  const prefix =
    cell.type === "select" ? "select_option" : "buttongroup_option";
  block.nextBlockId = option?.showFromId || `${prefix}_${cell.content.value}`;
}

// 답할 코멘트가 있는 요청 — 마쳤는지와 상관없이 빈 코멘트 답변 칸만 채운다 (buildUserSimRequest)
export function isReplyOnlyRequest(fields: UserSimField[]) {
  return fields.some(
    (field) => field.name === "comment_reply" && field.fill !== "none"
  );
}

// 빈 칸에 값을 채우고, 블록 진행과 같은 규칙으로 마지막 블록(또는 isEnd 블록)까지 연다.
// 값: fill 이 none 인 칸은 요청에 담긴 값(퀴즈 정답 등), 나머지는 Agent 값. 이미 채워진 칸은 덮어쓰지 않는다.
// 답할 코멘트가 있으면 답변 칸만 채운다 — 블록을 열거나 다른 칸을 건드리지 않는다.
export function applyAutoFill(
  content: ProgramContentType[],
  fields: UserSimField[],
  values: AutoFillValues
): ProgramContentType[] {
  const data = _.cloneDeep(content);
  const fieldMap = new Map(fields.map((field) => [field.key, field]));

  const fill = (cell: CellType, field?: UserSimField) => {
    if (!field || !isCellEmpty(cell)) return undefined;
    const filled = toCellContent(
      field,
      field.fill === "none" ? field.value : values[field.key],
      cell
    );
    if (filled) cell.content = { ...cell.content, ...filled };
    return filled;
  };

  if (isReplyOnlyRequest(fields)) {
    data.forEach((block, blockIndex) =>
      block.lines.forEach((line, lineIndex) =>
        line.forEach((cell, cellIndex) => {
          const field = fieldMap.get(
            getCellKey(blockIndex, lineIndex, cellIndex)
          );
          if (field?.name === "comment_reply") fill(cell, field);
        })
      )
    );
    return data;
  }

  // 고른 선택지의 칸을 블록 끝에 붙이고 채운다 (ButtonGroupMultipleChoiceCell 의 handleChange)
  const appendOptionContent = (
    block: ProgramContentType,
    cell: CellType,
    fieldKey: string
  ) => {
    const { options, selectedIndices } = cell.content as MultipleSelectionType;
    selectedIndices?.forEach((optionIndex) => {
      const additionalContent = options?.[optionIndex]?.additionalContent;
      if (!additionalContent) return;
      const lines = _.cloneDeep(additionalContent);
      lines.forEach((line, lineIndex) =>
        line.forEach((optionCell, cellIndex) =>
          fill(
            optionCell,
            fieldMap.get(
              getOptionCellKey(fieldKey, optionIndex, lineIndex, cellIndex)
            )
          )
        )
      );
      block.lines.push(...lines);
    });
  };

  let blockIndex: number | undefined = 0;

  while (blockIndex !== undefined) {
    const currentIndex: number = blockIndex;
    const block = data[currentIndex];
    block.isShown = true;
    block.isHidden = false;

    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        const field = fieldMap.get(
          getCellKey(currentIndex, lineIndex, cellIndex)
        );
        const filled = fill(cell, field);
        if (cell.type === "selfCheck" && filled) {
          // SelfCheckRadioButtonCell 의 handleChange 와 같은 다음 블록 지정
          block.nextBlockId = `${filled.selectedScore}`;
        } else if (field?.input === "choice" && filled) {
          setChoiceNextBlockId(block, cell, filled.selectedIndex as number);
        }
        if (field?.input === "multi_choice" && filled) {
          appendOptionContent(block, cell, field.key);
        }
        // 명상 오디오는 끝까지 들은 것으로 (AudioButtonWeb 의 setAudioEnded) — 마치기 검사가 다 들었는지 본다
        const audio = cell.content as AudioPlayerType;
        if (cell.type === "audioPlayer" && audio.audioUrl && !audio.isEnded) {
          cell.content = { ...audio, isEnded: true };
        }
        // 알림 허용 단계는 "허용 안 함" 으로 진행 (RequestPushNotificationButton 의 미허용 경로)
        const push = cell.content as PushNotificationType;
        if (cell.type === "pushNotification" && !push.clicked) {
          cell.content = { ...push, clicked: true };
          block.nextBlockId = push.showFromIdIfDenied;
        }
      })
    );

    blockIndex = getNextBlockIndex(data, currentIndex);
  }

  // 자동 계산 칸 (Cell.tsx 의 complementPercentageIdList 계산과 같음)
  data.forEach((block) =>
    block.lines.flat().forEach((cell) => {
      const idList = cell.content.complementPercentageIdList;
      if (cell.type === "percentage" && idList) {
        const percentage = Math.max(0, 100 - getPercentageSum(data, idList));
        cell.content = { ...cell.content, value: `${percentage}`, percentage };
      }
    })
  );

  return data;
}

// 다 쓴 값을 지금 내용에 넣을 수 있는지 — 넣을 내용(result)과 넣지 못하는 이유.
// baseContent: 요청을 만든 시점의 내용 — 칸 키가 이 배치 기준이라, 그사이 구조가 바뀌면(코멘트 추가 등) 넣지 않는다
export function resolveAutoFill({
  content,
  baseContent,
  fields,
  values,
}: {
  content: ProgramContentType[];
  baseContent: ProgramContentType[];
  fields: UserSimField[];
  values: AutoFillValues;
}) {
  const isStructureChanged = !isSameStructure(baseContent, content);
  const result = isStructureChanged
    ? undefined
    : applyAutoFill(content, fields, values);
  // 답할 코멘트가 있으면 답변 칸만 채우므로(마치기 전일 수도 있다) 마치기 조건 대신 답변 칸이 다 찼는지 본다
  const canApply =
    !!result &&
    (isReplyOnlyRequest(fields)
      ? fields.every(
          (field) =>
            field.fill === "none" || !!toCellContent(field, values[field.key])
        )
      : isAllUserFieldFilled(withFeedbackSent(result), false));
  const fieldMap = new Map(fields.map((field) => [field.key, field]));
  const invalidCount = Object.entries(values).filter(([key, value]) => {
    const field = fieldMap.get(key);
    return field && field.fill !== "none" && !toCellContent(field, value);
  }).length;
  return { result, canApply, isStructureChanged, invalidCount };
}
